
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

export const TermsAndConditionsDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          variant="link" 
          className="text-gray-400 p-0 h-auto text-[9px] font-normal hover:text-gray-500 hover:no-underline"
        >
          Terms and Conditions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Terms and Conditions for AI Surgical Analysis</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6 text-sm leading-relaxed">
            <section>
              <h3 className="font-semibold text-base mb-2">1. Purpose and Scope</h3>
              <p>This AI-powered surgical analysis tool ("the Service") is provided for educational and training purposes only. It is designed to assist medical professionals in the planning and discussion of surgical procedures but is not intended to replace professional medical judgment.</p>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">2. Educational Use Only</h3>
              <p>The Service and all its outputs, including but not limited to analyses, measurements, and recommendations, are to be used solely for educational and training purposes. Users acknowledge that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>The Service is not certified as a medical device</li>
                <li>Outputs should not be used as the sole basis for medical decisions</li>
                <li>All analyses should be independently verified by qualified healthcare providers</li>
                <li>The Service is not a substitute for professional medical training or expertise</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">3. No Medical Advice</h3>
              <p>The Service does not provide medical advice. Users understand that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>All analyses are generated through artificial intelligence and machine learning models</li>
                <li>Results may vary and are not guaranteed to be accurate or complete</li>
                <li>The Service should not be used for actual patient care decisions without independent verification</li>
                <li>Users maintain full responsibility for all medical decisions and their outcomes</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">4. User Responsibilities</h3>
              <p>Users of the Service agree to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Use the Service only for educational and training purposes</li>
                <li>Not rely solely on the Service for medical decisions</li>
                <li>Maintain patient confidentiality and privacy when using the Service</li>
                <li>Report any errors or inconsistencies observed in the analysis</li>
                <li>Keep their access credentials secure and not share them with unauthorized users</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">5. Liability and Disclaimer</h3>
              <p>By using the Service, users acknowledge and agree that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>The Service providers assume no responsibility for decisions made based on the analysis</li>
                <li>Users indemnify the Service providers against any claims arising from use of the Service</li>
                <li>The Service is provided "as is" without any warranties, express or implied</li>
                <li>Results may contain inaccuracies or errors and should be independently verified</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">6. Data Usage and Privacy</h3>
              <p>Users understand that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>The Service may collect and analyze user-provided data for improvement purposes</li>
                <li>No patient-identifying information should be uploaded to the Service</li>
                <li>Uploaded data may be used to improve the AI models and analysis capabilities</li>
                <li>Users are responsible for ensuring compliance with applicable privacy laws and regulations</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-base mb-2">7. Updates and Modifications</h3>
              <p>The Service providers reserve the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Modify these terms and conditions at any time</li>
                <li>Update the Service's functionality and features</li>
                <li>Suspend or terminate access to the Service at their discretion</li>
                <li>Modify or improve the AI models and analysis algorithms</li>
              </ul>
            </section>

            <section className="pb-4">
              <h3 className="font-semibold text-base mb-2">8. Acknowledgment</h3>
              <p>By using the Service, users acknowledge that they have read, understood, and agree to be bound by these terms and conditions. Users confirm their understanding that the Service is for educational purposes only and should not be used as a substitute for professional medical judgment.</p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
